import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { bidService } from '../services/bidService';
import { getAttachmentReadUrl, isPublicAttachmentUrl } from '../services/attachmentService';
import { updateBid, setCurrentBid } from '../store/slices/bidsSlice';
import type { RootState } from '../store';
import { BidStatus } from '../types';
import { CURRENCIES } from '../constants';
import { CountdownTimer } from '../components/CountdownTimer';
import { useBidSocket } from '../hooks/useBidSocket';
import { BackArrowIcon } from '../components/BackArrowIcon';
import { SubscriptionGateModal } from '../components/SubscriptionGateModal';
import {
  getLiveBidSubscriptionBlockMessage,
  hasValidBiddingSubscription,
} from '../utils/subscriptionAccess';

export function BidDetailPage() {
  const { bidId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    typeof location.state === 'object' &&
    location.state !== null &&
    'returnTo' in location.state &&
    typeof (location.state as { returnTo?: unknown }).returnTo === 'string'
      ? (location.state as { returnTo: string }).returnTo
      : null;
  const backPath = returnTo === '/history' ? '/history' : '/home';
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const currentUser = useSelector((s: RootState) => s.auth.user);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placeErr, setPlaceErr] = useState<string | null>(null);
  const [gateMessage, setGateMessage] = useState<string | null>(null);
  const liveGateShown = useRef(false);

  const { data: bid, isLoading, refetch } = useQuery({
    queryKey: ['bid', bidId],
    queryFn: () => bidService.getBid(bidId),
    enabled: Boolean(bidId),
    staleTime: 5000,
    refetchInterval: 5000,
  });

  useBidSocket(bidId, Boolean(bidId));

  useEffect(() => {
    if (bid) dispatch(setCurrentBid(bid));
  }, [bid, dispatch]);

  useEffect(() => {
    if (isLoading || !bid || liveGateShown.current) return;
    if (bid.status !== BidStatus.LIVE) return;
    if (bid.creatorId === currentUser?.id) return;
    const msg = getLiveBidSubscriptionBlockMessage(currentUser);
    if (!msg) return;
    liveGateShown.current = true;
    setGateMessage(msg);
  }, [bid, isLoading, currentUser]);

  if (isLoading || !bid) {
    return (
      <div className="py-12 text-center text-slate-500">
        <Link
          to={backPath}
          className="mx-auto mb-4 flex w-fit items-center gap-1.5 text-sm font-bold text-blue-600"
        >
          <BackArrowIcon />
          Back
        </Link>
        Loading...
      </div>
    );
  }

  const isCreator = bid.creatorId === currentUser?.id;
  const isAuthorized =
    isCreator ||
    Boolean(
      currentUser?.email &&
        bid.invitedEmails?.some((e) => e.toLowerCase() === currentUser.email.toLowerCase())
    );

  if (!isAuthorized) {
    return (
      <div className="py-12 text-center px-4">
        <Link
          to={backPath}
          className="mx-auto mb-4 flex w-fit items-center gap-1.5 text-sm font-bold text-blue-600"
        >
          <BackArrowIcon />
          Back
        </Link>
        <p className="text-red-600 font-semibold">Access denied. You are not invited to this bid.</p>
      </div>
    );
  }

  const currencyObj = CURRENCIES.find((c) => c.code === bid.item.currency) || CURRENCIES[0];
  const decrementalAmount = (bid.item.initialPrice * bid.decrementalPercentage) / 100;
  const nextBidAmount = Math.max(0, bid.currentLowestBid - decrementalAmount);
  const userBids = (bid.history ?? []).filter((h) => h.vendorName === currentUser?.name);
  const userCurrentBid = userBids.length > 0 ? userBids[0].amount : null;
  const isLeading = bid.currentWinner === currentUser?.name;
  const isRankOnlyForParticipant = !isCreator && Boolean(bid.rankOnlyMode);
  const attachments =
    Array.isArray(bid.attachments) && bid.attachments.length > 0
      ? bid.attachments
      : bid.attachmentUrl
        ? [{ fileName: bid.attachmentName || 'Attachment', fileUrl: bid.attachmentUrl }]
        : [];

  const openBidAttachment = async (fileUrl: string) => {
    try {
      const url = await getAttachmentReadUrl(fileUrl, bid.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Problem opening the document, please try after some time');
    }
  };

  const getUserRank = (): string => {
    if (!userCurrentBid || !currentUser?.name) return '-';
    const history = bid.history ?? [];
    const bestByVendor: Record<string, number> = {};
    for (const h of history) {
      const amt = typeof h.amount === 'number' ? h.amount : parseFloat(String(h.amount));
      if (!(h.vendorName in bestByVendor) || amt < bestByVendor[h.vendorName]) {
        bestByVendor[h.vendorName] = amt;
      }
    }
    const sorted = Object.entries(bestByVendor)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => a.amount - b.amount);
    let rank = 1;
    let prevAmount = -1;
    let userRank: number | null = null;
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].amount > prevAmount) {
        rank = i + 1;
        prevAmount = sorted[i].amount;
      }
      if (sorted[i].name === currentUser.name) {
        userRank = rank;
        break;
      }
    }
    if (userRank == null) return '-';
    const d = userRank % 10;
    const suffix =
      userRank >= 11 && userRank <= 13 ? 'th' : d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th';
    return `${userRank}${suffix}`;
  };

  const isUpcoming = bid.status === BidStatus.UPCOMING;
  const isLive = bid.status === BidStatus.LIVE;
  const isEnded = bid.status === BidStatus.ENDED;

  const placeBid = async () => {
    setConfirmOpen(false);
    setPlacing(true);
    setPlaceErr(null);
    try {
      const updated = await bidService.placeBid(bid.id, nextBidAmount);
      dispatch(updateBid(updated));
      dispatch(setCurrentBid(updated));
      queryClient.invalidateQueries({ queryKey: ['bid', bidId] });
      queryClient.invalidateQueries({ queryKey: ['bids'] });
      setTimeout(() => refetch(), 800);
    } catch (e: unknown) {
      setPlaceErr(e instanceof Error ? e.message : 'Failed');
      refetch();
    } finally {
      setPlacing(false);
    }
  };

  const tryPlaceBid = () => {
    setPlaceErr(null);
    if (!hasValidBiddingSubscription(currentUser)) {
      const msg = getLiveBidSubscriptionBlockMessage(currentUser);
      if (msg) setGateMessage(msg);
      return;
    }
    setConfirmOpen(true);
  };

  return (
    <div className="pb-8">
      <div className="flex items-center justify-between mb-4">
        <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500">
          <BackArrowIcon />
          Back
        </Link>
        <span
          className={`text-[9px] font-black uppercase px-3 py-1 rounded-full ${
            isLive ? 'bg-green-100 text-green-800' : isUpcoming ? 'bg-blue-100 text-blue-800' : 'bg-slate-100'
          }`}
        >
          {bid.status}
        </span>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-100 mb-4">
        <h1 className="text-lg font-black text-slate-900">{bid.item.name}</h1>
        <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">{bid.item.category}</p>
        <div className="mt-3 text-right">
          <p className="text-[8px] font-black text-slate-400 uppercase">Ceiling</p>
          <p className="text-base font-black text-slate-400 line-through">
            {currencyObj.symbol}
            {bid.item.initialPrice.toLocaleString()}
          </p>
          <p className="text-[8px] font-black text-blue-600">
            -{currencyObj.symbol}
            {decrementalAmount.toLocaleString()} ({bid.decrementalPercentage}%)
          </p>
        </div>
      </div>

      {isCreator && (
        <div className="bg-blue-600 text-white rounded-2xl p-4 mb-4 text-sm">
          <p className="text-[8px] font-black uppercase opacity-80">Creator</p>
          <p className="font-bold">Monitoring mode. Participation restricted.</p>
        </div>
      )}

      {(isLive || isUpcoming) && (
        <div
          className={`rounded-2xl p-4 mb-4 border text-center ${
            isLive ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'
          }`}
        >
          <div className="flex justify-center items-center gap-2 mb-2">
            <p className={`text-sm font-black uppercase ${isLive ? 'text-red-600' : 'text-blue-600'}`}>
              {isLive ? 'Time remaining' : 'Auction starts in'}
            </p>
            {isUpcoming && isCreator && (
              <Link
                to={`/create/${bid.id}`}
                className="text-[10px] font-black bg-slate-600 text-white px-3 py-1 rounded-lg ml-auto"
              >
                Edit
              </Link>
            )}
          </div>
          <CountdownTimer
            targetDate={new Date(isLive ? bid.endTime : bid.startTime)}
            className="text-lg"
          />
        </div>
      )}

      <div className="bg-white rounded-2xl p-4 border border-slate-100 mb-4">
        <p className="text-[10px] font-black text-slate-400 uppercase mb-2">Details</p>
        <p className="text-sm text-slate-600">{bid.item.description || 'No description.'}</p>
        {attachments.length > 0 && (
          <div className="mt-4 space-y-2">
            {attachments.map((a: { fileName?: string; fileUrl?: string }, i: number) =>
              isPublicAttachmentUrl(a.fileUrl) ? (
                <button
                  key={i}
                  type="button"
                  onClick={() => void openBidAttachment(a.fileUrl!)}
                  className="w-full min-w-0 flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm font-bold text-blue-800 text-left"
                >
                  <span className="min-w-0 flex-1 truncate">{a.fileName || 'File'}</span>
                </button>
              ) : (
                <div key={i} className="text-slate-400 text-sm italic max-w-full truncate">
                  {a.fileName} (unavailable)
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-100 mb-4">
        <p className="text-[10px] font-black text-slate-400 uppercase mb-3">
          {isEnded ? 'Final result' : 'Leaderboard'}
        </p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-[8px] font-bold text-slate-500 uppercase">Your bid</p>
            <p className="text-sm font-black">
              {userCurrentBid ? `${currencyObj.symbol}${userCurrentBid.toLocaleString()}` : '-'}
            </p>
          </div>
          <div className="bg-blue-50 rounded-xl p-3">
            <p className="text-[8px] font-bold text-slate-500 uppercase">Winning</p>
            {isRankOnlyForParticipant ? (
              <p className="text-xs font-bold text-slate-600">Hidden (rank only)</p>
            ) : (
              <p className={`text-sm font-black ${isLeading ? 'text-emerald-600' : ''}`}>
                {currencyObj.symbol}
                {bid.currentLowestBid.toLocaleString()}
              </p>
            )}
          </div>
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-[8px] font-bold text-slate-500 uppercase">Rank</p>
            <p className={`text-sm font-black ${isLeading ? 'text-emerald-600' : ''}`}>{getUserRank()}</p>
          </div>
        </div>
      </div>

      {isLive && !isCreator && (
        <div className="mb-4">
          {placeErr && <p className="text-red-600 text-sm mb-2 bg-red-50 p-3 rounded-xl">{placeErr}</p>}
          <div className="flex justify-between bg-emerald-50 border border-emerald-100 rounded-2xl p-4 mb-3">
            <div>
              <p className="text-[8px] font-black text-emerald-700 uppercase">Next offer</p>
              {isRankOnlyForParticipant ? (
                <p className="text-sm font-bold text-emerald-800">Hidden</p>
              ) : (
                <p className="text-xl font-black text-emerald-800">
                  {currencyObj.symbol}
                  {nextBidAmount.toLocaleString()}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[8px] font-black text-emerald-700 uppercase">Drop</p>
              {isRankOnlyForParticipant ? (
                <p className="text-sm font-bold">Locked</p>
              ) : (
                <p className="text-sm font-black text-emerald-800">
                  -{currencyObj.symbol}
                  {decrementalAmount.toLocaleString()}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={isLeading || placing}
            onClick={tryPlaceBid}
            className={`w-full py-4 rounded-2xl font-black text-white uppercase ${
              isLeading || placing ? 'bg-slate-300' : 'bg-blue-600 shadow-lg'
            }`}
          >
            {placing ? '...' : isLeading ? 'You are leading' : 'Place bid'}
          </button>
        </div>
      )}

      {isCreator && (
        <div className="bg-white rounded-2xl p-4 border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase mb-3">
            Bid history ({(bid.history ?? []).length})
          </p>
          {(bid.history ?? []).length === 0 ? (
            <p className="text-slate-400 text-sm italic">No bids yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {(bid.history ?? []).map((h, idx) => (
                <li key={h.id || idx} className="flex justify-between py-3 text-sm">
                  <div>
                    <p className="font-bold">{h.vendorName}</p>
                    {h.vendorCompanyName ? (
                      <p className="text-xs text-slate-500">{h.vendorCompanyName}</p>
                    ) : null}
                    <p className="text-xs text-slate-400">
                      {new Date(h.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <p className="font-black text-blue-600">
                    {currencyObj.symbol}
                    {h.amount.toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {gateMessage ? (
        <SubscriptionGateModal
          message={gateMessage}
          onOk={() => {
            setGateMessage(null);
            navigate('/subscription', { state: { returnTo: `/bid/${bidId}` } });
          }}
        />
      ) : null}

      {confirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center">
            <p className="text-lg font-black text-slate-900 mb-2">Confirm bid</p>
            <p className="text-slate-500 text-sm mb-4">Lower price to</p>
            <p className="text-3xl font-black text-blue-600 mb-6">
              {currencyObj.symbol}
              {nextBidAmount.toLocaleString()}
            </p>
            <button
              type="button"
              onClick={placeBid}
              className="w-full py-4 rounded-2xl bg-blue-600 text-white font-black mb-2"
            >
              Confirm
            </button>
            <button type="button" onClick={() => setConfirmOpen(false)} className="w-full py-3 text-slate-500 font-bold">
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
